import { useState, FormEvent, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { validateTaskForm, type TaskFormData, type TaskFormErrors } from '@/utils/validation';
import { validateTaskDependencies, type DependencyTask } from '@/utils/taskDependencyValidation';
import { adjustToBusinessDay, formatDateForInput, calculateEndDate, isDateWeekend } from '@/utils/dateUtils';
import { parseLocalDate } from '@/utils';
import { useProjects } from '@/hooks/useProjects';
import { useTeamMembers } from '@/hooks';
import type { Database } from '@/types/database.types';

type Task = Database['public']['Tables']['tasks']['Row'];

interface TaskFormProps {
    /** Whether the form modal is open */
    isOpen: boolean;
    /** Callback when form is closed/cancelled */
    onClose: () => void;
    /** Callback when form is submitted with valid data */
    onSubmit: (data: Omit<TaskFormData, 'project_id'> & { project_id: number }) => void;
    /** Project ID for the task (required for create mode) */
    projectId: number;
    /** Existing task data for edit mode (undefined for create mode) */
    initialData?: Task;
    /** Whether the submit action is in progress */
    isLoading?: boolean;
    /** Error message from submission */
    error?: string | null;
    /** All existing tasks in the project, used for dependency validation */
    existingTasks?: DependencyTask[];
}

/**
 * Task form component for creating and editing tasks
 * Handles both create and edit modes based on presence of initialData
 */
export const TaskForm = ({
    isOpen,
    onClose,
    onSubmit,
    projectId,
    initialData,
    isLoading = false,
    error = null,
    existingTasks = [],
}: TaskFormProps) => {
    const isEditMode = !!initialData;

    // Fetch projects and team members for dropdowns
    const { data: projects } = useProjects();
    const { data: teamMembers = [] } = useTeamMembers();

    // Form state
    const [formData, setFormData] = useState<TaskFormData>({
        project_id: projectId,
        name: initialData?.name ?? '',
        type: initialData?.type ?? '',
        size_unit: initialData?.size_unit ?? '',
        size_value: initialData?.size_value ?? '',
        start_date: initialData?.start_date ? formatDateForInput(initialData.start_date) : '',
        end_date: initialData?.end_date ? formatDateForInput(initialData.end_date) : '',
        status: initialData?.status ?? 'not_started',
        assigned_member_id: initialData?.assigned_member_id ?? null,
    });

    // Validation errors
    const [errors, setErrors] = useState<TaskFormErrors>({});
    const [dependencyError, setDependencyError] = useState<{ error: string; suggestion: string } | null>(null);

    // Track if a date was auto-adjusted from a weekend
    const [weekendAdjusted, setWeekendAdjusted] = useState(false);
    const [endWeekendAdjusted, setEndWeekendAdjusted] = useState(false);

    // Update form when initialData changes (for edit mode)
    useEffect(() => {
        if (initialData) {
            setFormData({
                project_id: initialData.project_id,
                name: initialData.name,
                type: initialData.type,
                size_unit: initialData.size_unit,
                size_value: initialData.size_value,
                start_date: formatDateForInput(initialData.start_date),
                end_date: formatDateForInput(initialData.end_date),
                status: initialData.status,
                assigned_member_id: initialData.assigned_member_id,
            });
        } else {
            // Reset to defaults for create mode
            setFormData({
                project_id: projectId,
                name: '',
                type: '',
                size_unit: '',
                size_value: '',
                start_date: '',
                end_date: '',
                status: 'not_started',
                assigned_member_id: null,
            });
        }
        setErrors({});
        setDependencyError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.id, projectId]); // Only depend on ID to avoid resetting during typing

    // Handle input changes
    const handleChange = (field: keyof TaskFormData, value: string | number | null) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        if (field === 'type' || field === 'start_date' || field === 'end_date') {
            setDependencyError(null);
        }
    };

    // Handle start date change with weekend exclusion
    const handleStartDateChange = (date: string) => {
        if (!date) {
            handleChange('start_date', date);
            setWeekendAdjusted(false);
            return;
        }

        const start = parseLocalDate(date);
        const isWeekendDate = isDateWeekend(start);
        const adjustedStart = adjustToBusinessDay(start);

        setWeekendAdjusted(isWeekendDate);
        handleChange('start_date', formatDateForInput(adjustedStart));

        // If end date exists and size is set, recalculate end date
        if (formData.end_date && formData.size_unit && formData.size_value) {
            const duration = typeof formData.size_value === 'string'
                ? parseFloat(formData.size_value)
                : formData.size_value;

            if (formData.size_unit === 'days' && duration > 0) {
                const newEndDate = calculateEndDate(adjustedStart, duration);
                handleChange('end_date', formatDateForInput(newEndDate));
                setEndWeekendAdjusted(false);
            }
        }
    };

    // Handle end date change with weekend exclusion (AC8: mirror start-date behavior)
    const handleEndDateChange = (date: string) => {
        if (!date) {
            handleChange('end_date', date);
            setEndWeekendAdjusted(false);
            return;
        }

        const end = parseLocalDate(date);
        const isWeekendDate = isDateWeekend(end);
        const adjustedEnd = adjustToBusinessDay(end);

        setEndWeekendAdjusted(isWeekendDate);
        handleChange('end_date', formatDateForInput(adjustedEnd));
    };

    // Handle size change - recalculate end date if start date is set
    const handleSizeChange = (unit: 'hours' | 'days' | '', value: number | string) => {
        handleChange('size_unit', unit);
        handleChange('size_value', value);
        
        // If days unit and start date is set, recalculate end date
        if (unit === 'days' && formData.start_date && value) {
            const start = new Date(formData.start_date);
            const adjustedStart = adjustToBusinessDay(start);
            const duration = typeof value === 'string' ? parseFloat(value) : value;
            
            if (duration > 0) {
                const newEndDate = calculateEndDate(adjustedStart, duration);
                handleChange('end_date', formatDateForInput(newEndDate));
            }
        }
    };

    // Handle form submission
    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        // Ensure project_id is set
        const formDataWithProject: TaskFormData = {
            ...formData,
            project_id: projectId,
        };

        // Get selected team member's role for validation
        const selectedMember = teamMembers.find(
            (member) => member.id === formData.assigned_member_id
        );
        const teamMemberRole = selectedMember?.role;

        // Validate form (including task assignment validation)
        const validation = validateTaskForm(formDataWithProject, teamMemberRole);

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        // Validate task dependency rules
        if (formData.start_date && formData.end_date && formData.type) {
            const taskForValidation: DependencyTask = {
                id: initialData?.id ?? 0,
                project_id: projectId,
                type: formData.type as DependencyTask['type'],
                start_date: formData.start_date,
                end_date: formData.end_date,
            };
            const depResult = validateTaskDependencies(taskForValidation, existingTasks);
            if (!depResult.valid) {
                setDependencyError({
                    error: depResult.error ?? 'Dependency violation',
                    suggestion: depResult.suggestion ?? '',
                });
                return;
            }
        }
        setDependencyError(null);

        // Adjust dates to business days if they fall on weekends
        const startDate = new Date(formData.start_date);
        const endDate = new Date(formData.end_date);
        const adjustedStart = adjustToBusinessDay(startDate);
        const adjustedEnd = adjustToBusinessDay(endDate);

        // Submit valid data
        onSubmit({
            project_id: projectId,
            name: formData.name.trim(),
            type: formData.type as 'design' | 'development' | 'integration' | 'code_review' | 'qa',
            size_unit: formData.size_unit as 'hours' | 'days',
            size_value: typeof formData.size_value === 'string' 
                ? parseFloat(formData.size_value) 
                : formData.size_value,
            start_date: formatDateForInput(adjustedStart),
            end_date: formatDateForInput(adjustedEnd),
            status: formData.status as 'not_started' | 'in_progress' | 'completed' | 'blocked',
            assigned_member_id: formData.assigned_member_id || null,
        });
    };

    // Reset form when modal closes
    const handleClose = () => {
        setFormData({
            project_id: projectId,
            name: '',
            type: '',
            size_unit: '',
            size_value: '',
            start_date: '',
            end_date: '',
            status: 'not_started',
            assigned_member_id: null,
        });
        setErrors({});
        setDependencyError(null);
        setWeekendAdjusted(false);
        setEndWeekendAdjusted(false);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={isEditMode ? 'Edit Task' : 'Create New Task'}
            size="lg"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* API Error Alert */}
                {error && (
                    <Alert variant="error" className="mb-4">
                        {error}
                    </Alert>
                )}

                {/* Dependency Violation Alert */}
                {dependencyError && (
                    <Alert variant="error" className="mb-4" title="Dependency Rule Violation">
                        <p>{dependencyError.error}</p>
                        {dependencyError.suggestion && (
                            <p className="mt-1 text-sm font-medium">{dependencyError.suggestion}</p>
                        )}
                    </Alert>
                )}

                {/* Task Name Field */}
                <Input
                    id="task-name"
                    label="Task Name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Enter task name"
                    disabled={isLoading}
                    error={errors.name}
                    required
                    aria-label="Task name"
                />

                {/* Project Selection (disabled in create mode, shown for reference) */}
                {projects && (
                    <Select
                        id="task-project"
                        label="Project"
                        value={String(formData.project_id)}
                        onChange={(value) => handleChange('project_id', parseInt(value, 10))}
                        options={projects.map((project) => ({
                            value: project.id,
                            label: project.name,
                        }))}
                        placeholder="Select a project..."
                        disabled={isLoading || isEditMode}
                        error={errors.project_id}
                        required
                    />
                )}

                {/* Task Type Selection */}
                <Select
                    id="task-type"
                    label="Task Type"
                    value={formData.type}
                    onChange={(value) => handleChange('type', value)}
                    options={[
                        { value: 'design', label: 'Design' },
                        { value: 'development', label: 'Development' },
                        { value: 'integration', label: 'Integration' },
                        { value: 'code_review', label: 'Code Review' },
                        { value: 'qa', label: 'QA' },
                    ]}
                    placeholder="Select a type..."
                    disabled={isLoading}
                    error={errors.type}
                    required
                />

                {/* Size Input with Unit Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Size <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                        {/* Unit Radio Selection */}
                        <div className="flex gap-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="size-unit"
                                    value="hours"
                                    checked={formData.size_unit === 'hours'}
                                    onChange={() => handleSizeChange('hours', formData.size_value)}
                                    disabled={isLoading}
                                    className="mr-2"
                                    aria-label="Hours"
                                />
                                <span className="text-sm text-gray-700">Hours</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="size-unit"
                                    value="days"
                                    checked={formData.size_unit === 'days'}
                                    onChange={() => handleSizeChange('days', formData.size_value)}
                                    disabled={isLoading}
                                    className="mr-2"
                                    aria-label="Days"
                                />
                                <span className="text-sm text-gray-700">Days</span>
                            </label>
                        </div>
                        {/* Size Value Input */}
                        <div className="flex-1">
                            <Input
                                id="task-size"
                                label="Task size value"
                                type="number"
                                min="1"
                                max={formData.size_unit === 'hours' ? '999' : '365'}
                                value={formData.size_value}
                                onChange={(e) => handleSizeChange(formData.size_unit as 'hours' | 'days', e.target.value)}
                                placeholder={formData.size_unit === 'hours' ? '1-999' : '1-365'}
                                disabled={isLoading || !formData.size_unit}
                                error={errors.size_value}
                                className="w-full"
                            />
                        </div>
                    </div>
                    {errors.size_unit && (
                        <p className="mt-1 text-sm text-red-600" role="alert">
                            {errors.size_unit}
                        </p>
                    )}
                </div>

                {/* Start Date */}
                <div>
                    <Input
                        id="task-start-date"
                        label="Start Date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        disabled={isLoading}
                        error={errors.start_date}
                        required
                        aria-label="Task start date"
                    />
                    {weekendAdjusted && (
                        <p className="text-xs text-amber-800 mt-1" role="status">
                            Weekend date auto-adjusted to next business day
                        </p>
                    )}
                </div>

                {/* End Date */}
                <div>
                    <Input
                        id="task-end-date"
                        label="End Date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        disabled={isLoading}
                        error={errors.end_date}
                        required
                        aria-label="Task end date"
                    />
                    {endWeekendAdjusted && (
                        <p className="text-xs text-amber-800 mt-1" role="status">
                            Weekend date auto-adjusted to next business day
                        </p>
                    )}
                </div>

                {/* Status Selection */}
                <Select
                    id="task-status"
                    label="Status"
                    value={formData.status}
                    onChange={(value) => handleChange('status', value)}
                    options={[
                        { value: 'not_started', label: 'Not Started' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'blocked', label: 'Blocked' },
                    ]}
                    disabled={isLoading}
                    error={errors.status}
                    required
                />

                {/* Team Member Selection (Optional) */}
                <Select
                    id="task-assigned-member"
                    label="Assigned Team Member"
                    helperText="Optional"
                    value={formData.assigned_member_id ? String(formData.assigned_member_id) : ''}
                    onChange={(value) => handleChange('assigned_member_id', value ? parseInt(value, 10) : null)}
                    options={[
                        { value: '', label: 'Unassigned' },
                        ...teamMembers.map((member) => ({
                            value: member.id,
                            label: `${member.name} (${member.role.charAt(0).toUpperCase() + member.role.slice(1)})`,
                        })),
                    ]}
                    disabled={isLoading}
                    error={errors.assigned_member_id}
                />

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        onClick={handleClose}
                        variant="secondary"
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {isLoading ? 'Saving...' : isEditMode ? 'Update Task' : 'Create Task'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

TaskForm.displayName = 'TaskForm';

